import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackEvent, identifyUser, EVENTS } from '@/lib/analytics';
import { getCapturedUtms } from '@/lib/utm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    admin_code: ''
  });
  const [loading, setLoading] = useState(false);

  // Fix #4: Admin Code field is hidden by default to avoid looking phishy on the
  // public signup form. To create an admin account, visit /login?admin=1 (or any
  // BragBox URL with the ?admin=1 query param). Admin onboarding URL pattern:
  //   https://bragbox.ai/login?admin=1
  // The ADMIN_AUTH_FIX_SUMMARY.md backend doc describes how the admin_code is
  // verified on the server; this field merely surfaces the input.
  const showAdminField = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('admin') === '1';

  useEffect(() => {
    // Check if already logged in
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken) {
      navigate('/dashboard', { replace: true });
    }

    // Fix #5: Default to LOGIN mode for /login. Only show signup when URL is /signup
    // (or /register), or when an explicit ?signup=true / ?from=onboarding param is set.
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname.toLowerCase();
    const isSignupRoute = path === '/signup' || path === '/register' || path.startsWith('/signup/') || path.startsWith('/register/');
    const wantsSignup = isSignupRoute
      || params.get('signup') === 'true'
      || params.get('from') === 'onboarding';

    setIsSignup(wantsSignup);

    // Google OAuth failures redirect back here with ?error=oauth_failed so the
    // user sees a clean message instead of raw JSON from the API.
    if (params.get('error') === 'oauth_failed') {
      toast.error('Google sign in did not complete. Please try again, or use your email and password.');
      // Strip the error param so a refresh doesn't re-toast.
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [navigate]);

  const handleGoogleLogin = () => {
    // Redirect to auth-callback which will handle routing to dashboard after authentication
    const redirectUrl = window.location.origin + '/auth-callback';
    window.location.href = `${BACKEND_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (isSignup && !formData.name) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    if (isSignup) {
      trackEvent(EVENTS.SIGNUP_STARTED, { method: 'email' });
    }
    try {
      const endpoint = isSignup ? '/auth/signup' : '/auth/login';
      // For signup, attach the UTMs we captured on landing. See lib/utm.js.
      // The backend stores these on the user row so /admin/stats can break out
      // signups by source.
      const requestBody = isSignup
        ? { ...formData, ...getCapturedUtms() }
        : formData;
      const response = await axios.post(`${API}${endpoint}`, requestBody);

      const { session_token, user } = response.data;

      if (!session_token) {
        throw new Error('No session token received');
      }

      // Store session token
      localStorage.setItem('session_token', session_token);

      if (user && user.user_id) {
        identifyUser(user.user_id, { email: user.email, is_admin: user.is_admin });
      }
      if (isSignup) {
        trackEvent(EVENTS.SIGNUP_COMPLETED, { method: 'email' });
      }

      toast.success(isSignup ? 'Account created!' : 'Welcome back!');
      
      // Handle new signups: Free tier goes to onboarding, paid plan selection goes to pricing
      if (isSignup) {
        const params = new URLSearchParams(window.location.search);
        const planParam = params.get('plan');
        
        // If user selected a paid plan during signup, redirect to pricing
        if (planParam && planParam !== 'free') {
          console.log('→ NEW USER: Selected paid plan, redirecting to pricing');
          navigate(`/pricing?plan=${planParam}&auto=true`, { replace: true });
          return;
        }
        
        // Free tier users go directly to onboarding (subscription_plan='free' set by backend)
        console.log('→ NEW USER: Free tier, redirecting to onboarding');
        navigate('/onboarding', { replace: true });
        return;
      }
      
      // Existing user login (not signup) - normal flow
      console.log('User data:', user);
      
      // CRITICAL: Check if user is admin FIRST
      if (user.is_admin === true) {
        console.log('→ ADMIN USER: Redirecting to /admin');
        navigate('/admin', { replace: true });
        return;
      }
      
      if (!user.onboarding_completed) {
        navigate('/onboarding', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Auth error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Authentication failed';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Handle Pydantic validation errors
        if (data.detail && Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => err.msg).join(', ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        }
      }
      
      toast.error(errorMessage);
      
      if (errorMessage.includes('Google')) {
        toast.error('This account uses Google login. Please use "Continue with Google" instead.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--navy-50)' }}>
      <div className="max-w-md w-full bg-white p-8 rounded-lg border border-gray-200 shadow-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-gray-600">
            {isSignup ? 'Create your account to start tracking wins' : 'Sign in to continue to BragBox'}
          </p>
        </div>

        {!showEmailLogin ? (
          <div className="space-y-4">
            <Button
              data-testid="google-login-btn"
              onClick={handleGoogleLogin}
              className="w-full py-6 text-base font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: 'var(--navy-900)', 
                color: 'white'
              }}
            >
              <LogIn className="w-5 h-5" />
              {isSignup ? 'Sign Up with Google' : 'Continue with Google'}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>

            <Button
              onClick={() => setShowEmailLogin(true)}
              variant="outline"
              className="w-full py-6 text-base font-medium rounded-lg transition-colors duration-200"
            >
              {isSignup ? 'Sign Up with Email' : 'Continue with Email'}
            </Button>
            
            <div className="text-center pt-4">
              <button
                onClick={() => setIsSignup(!isSignup)}
                className="text-sm"
                style={{ color: 'var(--navy-600)' }}
              >
                {isSignup ? 'Already have an account? Sign in' : 'New to BragBox? Create account'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {isSignup && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                  Name
                </label>
                <Input
                  data-testid="name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                Email
              </label>
              <Input
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="w-full"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                  Password
                </label>
                {!isSignup && (
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm"
                    style={{ color: 'var(--navy-600)' }}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="w-full"
              />
            </div>

            {/* Fix #4: Admin Code field only renders when URL has ?admin=1 */}
            {showAdminField && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                  Admin Code (optional)
                </label>
                <Input
                  type="password"
                  value={formData.admin_code || ''}
                  onChange={(e) => setFormData({ ...formData, admin_code: e.target.value })}
                  placeholder="Enter admin code if you have one"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Have an admin code? Enter it to get admin access</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 text-base font-medium rounded-lg transition-colors duration-200"
              style={{ 
                backgroundColor: 'var(--navy-900)', 
                color: 'white'
              }}
            >
              {loading ? 'Please wait...' : (isSignup ? 'Create Account' : 'Sign In')}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setIsSignup(!isSignup)}
                className="text-sm" 
                style={{ color: 'var(--navy-600)' }}
              >
                {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowEmailLogin(false)}
                className="text-sm text-gray-600 hover:text-navy-900 transition-colors duration-200"
              >
                ← Back to other options
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 hover:text-navy-900 transition-colors duration-200"
          >
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;